import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createTranslator } from "next-intl";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import ImageDropUpload from "../../../shared/ImageDropUpload";
import { useClaims } from "../ClaimsProvider";
import { Overview } from "../components/Overview";
import {
  ExchangeModeToggle,
  ExchangeProductPicker,
} from "../components/ExchangeProductPicker";
import { ProductTitle } from "./components/ProductTitle";
import {
  canFileInsuranceClaim,
  canRequestReturn,
  EXCHANGE_MODES,
  getExchangeLabel,
  getIssueOptions,
  getResolutionOptions,
  isExchangeResolution,
  isImageUploadRequired,
  resolveExchangeMode,
  siblingExchangeVariants,
} from "../components/options";
import { useExchangeCatalog } from "../useExchangeCatalog";
import content from "../../../content/claims.json";
import { baseTheme } from "../../../themes/shipinsure";
import { itemSectionsForClaimForm } from "../utils/packages";
import { PackageSection } from "../components/PackageSection";
import {
  applyPackageItemValues,
  PackageClaimFields,
} from "../components/PackageClaimFields";

export function ResolveClaim() {
  const {
    claimsForm,
    order,
    setActiveStep,
    locale,
    resetClaimForm,
    setIsSelectAll,
  } = useClaims();
  const {
    setValue,
    getValues,
    register,
    isSubmitting,
    control,
    watch,
    handleSubmit,
    formState: {
      isDirty,
      errors: { items: itemsErrors, details: detailsError },
    },
  } = claimsForm;

  const itemsWatched = watch("items") ?? [];
  const showSharedDetailsField = itemsWatched.some((row) => !!row?.issueType);

  const t = createTranslator({
    messages: content[locale],
  });

  const { showPackageSections, itemSections } = itemSectionsForClaimForm(order);
  const insuranceAllowed = canFileInsuranceClaim(order);
  const returnsAllowed = canRequestReturn(order);
  const [selectAllPackageIds, setSelectAllPackageIds] = useState([]);
  const { catalog, status: catalogStatus } = useExchangeCatalog(order);

  return (
    <>
      <Overview />
      <form
        onSubmit={handleSubmit((values) => {
          if (values.items?.filter((v) => !!v.reason).length) {
            setActiveStep(2);
          }
        })}
      >
        <Box mt="32px">
          {itemSections.map((group) => {
            const selectAllActive = selectAllPackageIds.includes(group.id);
            const showSelectAll = showPackageSections && group.items.length > 1;

            return (
              <PackageSection
                key={group.id}
                group={group}
                showHeader={showPackageSections}
                t={t}
                showSelectAll={showSelectAll}
                selectAllActive={selectAllActive}
                selectAllDisabled={!insuranceAllowed}
                onToggleSelectAll={() => {
                  if (selectAllActive) {
                    applyPackageItemValues(group, setValue, {
                      issueType: "",
                      reason: "",
                      resolution: "",
                      images: [],
                    });
                    setSelectAllPackageIds((ids) =>
                      ids.filter((id) => id !== group.id),
                    );
                    return;
                  }

                  applyPackageItemValues(group, setValue, {
                    issueType: "insurance",
                    reason: "",
                    resolution: "",
                    images: [],
                  });
                  setSelectAllPackageIds((ids) => [...ids, group.id]);
                }}
              >
                {selectAllActive ? (
                  <PackageClaimFields
                    group={group}
                    order={order}
                    t={t}
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    register={register}
                    watch={watch}
                    itemsErrors={itemsErrors}
                  />
                ) : (
                  group.items.map(({ lineItem: item, formIndex: index }) => {
                    const currentIssueType = watch(`items.${index}.issueType`);
                    const currentReason = watch(`items.${index}.reason`);
                    const currentResolution = watch(`items.${index}.resolution`);
                    const issueOptions = getIssueOptions(
                      currentIssueType,
                      order.merchant_issues,
                    );
                    const exchangeLabel = getExchangeLabel(
                      issueOptions,
                      currentReason,
                    );
                    const siblings = siblingExchangeVariants(
                      catalog,
                      item.product_id,
                      item.variant_id,
                    );
                    const sameItemAvailable =
                      catalogStatus === "loading" || siblings.length > 0;
                    const exchangeMode = resolveExchangeMode(
                      watch(`items.${index}.exchangeMode`),
                      sameItemAvailable,
                    );

                    const resolutionOptions = getResolutionOptions(
                      issueOptions,
                      currentReason,
                      order.store_credits_enabled,
                      order.allow_exchanges,
                    );
                    const showExchangePicker = isExchangeResolution(
                      currentResolution,
                      exchangeLabel,
                    );
                    const label =
                      currentIssueType === "return"
                        ? "What are you returning this item?*"
                        : `${t("resolve.form.issue")}*`;

                    return (
                      <Box
                        mb="16px"
                        p="24px"
                        bgcolor="#F4F4F9"
                        borderRadius="8px"
                        key={`${group.id}-${index}`}
                      >
                        <Grid container>
                          <ProductTitle
                            item={item}
                            title={t("resolve.variant")}
                          />
                          <Grid
                            item
                            xs={12}
                            md={6}
                            textAlign="right"
                            mt={{ xs: "15px", md: "0px" }}
                          >
                            {returnsAllowed && (
                              <Box>
                                <Button
                                  sx={{ width: { xs: "100%", md: "175px" } }}
                                  size="small"
                                  variant={
                                    currentIssueType === "return"
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() => {
                                    setValue(
                                      `items.${index}.issueType`,
                                      currentIssueType === "return"
                                        ? ""
                                        : "return",
                                    );
                                    setValue(`items.${index}.itemId`, item.id);
                                    setValue(`items.${index}.reason`, "");
                                    setValue(`items.${index}.resolution`, "");
                                    setValue(`items.${index}.exchangeVariantId`, "");
                                    setValue(
                                      `items.${index}.exchangeVariantTitle`,
                                      "",
                                    );
                                    setValue(`items.${index}.images`, []);
                                  }}
                                >
                                  Return this item
                                </Button>
                              </Box>
                            )}
                            <Box mt="10px">
                              <Tooltip
                                title={
                                  insuranceAllowed
                                    ? ""
                                    : t.rich("resolve.order.uncoveredReturnsAllowed", {
                                        text: (chunk) => <Box>{chunk}</Box>,
                                      })
                                }
                                enterTouchDelay={0}
                                arrow
                              >
                                <Box component="span" sx={{ display: "inline-block" }}>
                                  <Button
                                    sx={{
                                      pl: 2,
                                      pr: 2,
                                      maxWidth: "300px",
                                    }}
                                    size="small"
                                    variant={
                                      currentIssueType === "insurance"
                                        ? "contained"
                                        : "outlined"
                                    }
                                    onClick={() => {
                                      setValue(
                                        `items.${index}.issueType`,
                                        currentIssueType === "insurance"
                                          ? ""
                                          : "insurance",
                                      );
                                      setValue(`items.${index}.itemId`, item.id);
                                      setValue(`items.${index}.reason`, "");
                                      setValue(`items.${index}.resolution`, "");
                                      setValue(`items.${index}.exchangeVariantId`, "");
                                      setValue(`items.${index}.exchangeVariantTitle`, "");
                                      setValue(`items.${index}.images`, []);
                                    }}
                                    disabled={!insuranceAllowed}
                                  >
                                    {t("resolve.form.toggle")}
                                  </Button>
                                </Box>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                        <Box>
                          {currentIssueType && (
                            <Box mt="30px">
                              <Grid container rowSpacing={2}>
                                {item.quantity > 1 && (
                                  <Grid item xs={12}>
                                    <Controller
                                      name={`items.${index}.quantity`}
                                      control={control}
                                      defaultValue={item.quantity}
                                      rules={{
                                        validate: (value) => {
                                          const n = Number(value);
                                          if (
                                            currentIssueType &&
                                            (isNaN(n) ||
                                              n < 1 ||
                                              n > item.quantity)
                                          ) {
                                            return t("resolve.required");
                                          }
                                          return true;
                                        },
                                      }}
                                      render={({ field }) => {
                                        const qtyLabel = `${t("resolve.form.quantity")}*`;
                                        return (
                                          <FormControl
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 2 }}
                                            error={getFieldError(
                                              itemsErrors,
                                              index,
                                              "quantity",
                                            )}
                                          >
                                            <InputLabel>{qtyLabel}</InputLabel>
                                            <Select
                                              label={qtyLabel}
                                              value={
                                                field.value ?? item.quantity
                                              }
                                              onChange={field.onChange}
                                            >
                                              {Array.from(
                                                { length: item.quantity },
                                                (_, i) => i + 1,
                                              ).map((n) => (
                                                <MenuItem key={n} value={n}>
                                                  {n}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                            <FormHelperText>
                                              {t("resolve.form.quantityHelper")}
                                            </FormHelperText>
                                          </FormControl>
                                        );
                                      }}
                                    />
                                    {getErrorMessage(
                                      itemsErrors,
                                      index,
                                      "quantity",
                                    )}
                                  </Grid>
                                )}
                                <Grid item xs={12}>
                                  <Controller
                                    name={`items.${index}.reason`}
                                    defaultValue=""
                                    control={control}
                                    rules={{
                                      validate: (value) =>
                                        currentIssueType && !value
                                          ? t("resolve.required")
                                          : true,
                                    }}
                                    render={({ field }) => (
                                      <FormControl fullWidth>
                                        <InputLabel>{label}</InputLabel>
                                        <Select
                                          label={label}
                                          value=""
                                          {...field}
                                          onChange={(value) => {
                                            field.onChange(value);
                                            setValue(
                                              `items.${index}.images`,
                                              [],
                                            );
                                            setValue(
                                              `items.${index}.resolution`,
                                              "",
                                            );
                                            setValue(
                                              `items.${index}.exchangeVariantId`,
                                              "",
                                            );
                                            setValue(
                                              `items.${index}.exchangeVariantTitle`,
                                              "",
                                            );
                                          }}
                                          error={getFieldError(
                                            itemsErrors,
                                            index,
                                            "reason",
                                          )}
                                        >
                                          {issueOptions.map((issue, i) => (
                                            <MenuItem
                                              key={i}
                                              value={issue.issue_name}
                                            >
                                              {issue.issue_name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                        {getErrorMessage(
                                          itemsErrors,
                                          index,
                                          "reason",
                                        )}
                                      </FormControl>
                                    )}
                                  />
                                </Grid>
                                {isImageUploadRequired(
                                  currentReason,
                                  order.merchant_issues,
                                ) && (
                                  <Grid item xs={12}>
                                    <ImageDropUpload
                                      images={
                                        getValues(`items.${index}.images`) ?? []
                                      }
                                      onImageChange={(images) => {
                                        setValue(
                                          `items.${index}.images`,
                                          images,
                                          {
                                            shouldValidate: images.length
                                              ? true
                                              : false,
                                          },
                                        );
                                      }}
                                      text={t.rich("resolve.upload", {
                                        text: (chunk) => (
                                          <Typography>{chunk}</Typography>
                                        ),
                                        cta: (chunk) => (
                                          <Typography
                                            component="span"
                                            color={baseTheme.colors.primary}
                                          >
                                            {chunk}
                                          </Typography>
                                        ),
                                      })}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(`items.${index}.images`, {
                                        validate: (value) => {
                                          return value.length === 0
                                            ? t("resolve.required")
                                            : true;
                                        },
                                      })}
                                    />
                                    {getErrorMessage(
                                      itemsErrors,
                                      index,
                                      "images",
                                    )}
                                  </Grid>
                                )}
                                {currentReason && (
                                  <Grid item xs={12}>
                                    <Controller
                                      name={`items.${index}.resolution`}
                                      control={control}
                                      defaultValue=""
                                      rules={{
                                        validate: (value) =>
                                          currentIssueType && !value
                                            ? t("resolve.required")
                                            : true,
                                      }}
                                      render={({ field }) => (
                                        <FormControl fullWidth>
                                          <InputLabel>
                                            {t("resolve.form.resolution")}*
                                          </InputLabel>
                                          <Select
                                            label={t("resolve.form.resolution")}
                                            value=""
                                            {...field}
                                            onChange={(event) => {
                                              field.onChange(event);
                                              if (
                                                !isExchangeResolution(
                                                  event.target.value,
                                                  exchangeLabel,
                                                )
                                              ) {
                                                setValue(
                                                  `items.${index}.exchangeVariantId`,
                                                  "",
                                                );
                                                setValue(
                                                  `items.${index}.exchangeVariantTitle`,
                                                  "",
                                                );
                                              }
                                            }}
                                            error={getFieldError(
                                              itemsErrors,
                                              index,
                                              "resolution",
                                            )}
                                          >
                                            {resolutionOptions.map(
                                              (option, i) => (
                                                <MenuItem
                                                  key={i}
                                                  value={option}
                                                >
                                                  {option}
                                                </MenuItem>
                                              ),
                                            )}
                                          </Select>
                                          {getErrorMessage(
                                            itemsErrors,
                                            index,
                                            "resolution",
                                          )}
                                        </FormControl>
                                      )}
                                    />
                                  </Grid>
                                )}
                                {showExchangePicker && (
                                  <Grid item xs={12}>
                                    <ExchangeModeToggle
                                      mode={exchangeMode}
                                      sameItemAvailable={sameItemAvailable}
                                      onChange={(mode) => {
                                        setValue(`items.${index}.exchangeMode`, mode);
                                        setValue(`items.${index}.exchangeVariantId`, "");
                                        setValue(
                                          `items.${index}.exchangeVariantTitle`,
                                          "",
                                        );
                                      }}
                                      t={t}
                                    />
                                    {exchangeMode === EXCHANGE_MODES.DIFFERENT_ITEM && (
                                      <Controller
                                        name={`items.${index}.exchangeVariantId`}
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                          validate: (value) =>
                                            value ? true : t("resolve.required"),
                                        }}
                                        render={({ field }) => (
                                          <ExchangeProductPicker
                                            shipInsureId={order.shipInsureID}
                                            lineItemId={String(item.id)}
                                            value={field.value ?? ""}
                                            selectedTitle={watch(
                                              `items.${index}.exchangeVariantTitle`,
                                            )}
                                            hasError={getFieldError(
                                              itemsErrors,
                                              index,
                                              "exchangeVariantId",
                                            )}
                                            onSelect={(variantId, title) => {
                                              field.onChange(variantId);
                                              setValue(
                                                `items.${index}.exchangeVariantTitle`,
                                                title,
                                              );
                                            }}
                                            t={t}
                                          />
                                        )}
                                      />
                                    )}
                                    {exchangeMode === EXCHANGE_MODES.SAME_ITEM && (
                                      <Controller
                                        name={`items.${index}.exchangeVariantId`}
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                          validate: (value) =>
                                            showExchangePicker && !value
                                              ? t("resolve.required")
                                              : true,
                                        }}
                                        render={({ field }) => (
                                          <FormControl
                                            fullWidth
                                            error={getFieldError(
                                              itemsErrors,
                                              index,
                                              "exchangeVariantId",
                                            )}
                                          >
                                            <InputLabel>
                                              {t("resolve.form.exchange")}*
                                            </InputLabel>
                                            <Select
                                              label={`${t("resolve.form.exchange")}*`}
                                              value={field.value ?? ""}
                                              onChange={(event) => {
                                                const variant = siblings.find(
                                                  (option) =>
                                                    option.variant_id ===
                                                    event.target.value,
                                                );
                                                field.onChange(event.target.value);
                                                setValue(
                                                  `items.${index}.exchangeVariantTitle`,
                                                  variant?.title || "",
                                                );
                                              }}
                                              disabled={
                                                catalogStatus !== "ready" ||
                                                siblings.length === 0
                                              }
                                            >
                                              {siblings.map((variant) => (
                                                <MenuItem
                                                  key={variant.variant_id}
                                                  value={variant.variant_id}
                                                >
                                                  {variant.title ||
                                                    variant.variant_id}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                            <FormHelperText>
                                              {catalogStatus === "loading"
                                                ? t("resolve.form.exchangeLoading")
                                                : siblings.length === 0
                                                  ? t(
                                                      "resolve.form.exchangeUnavailable",
                                                    )
                                                  : t(
                                                      "resolve.form.exchangeHelper",
                                                    )}
                                            </FormHelperText>
                                          </FormControl>
                                        )}
                                      />
                                    )}
                                    {getErrorMessage(
                                      itemsErrors,
                                      index,
                                      "exchangeVariantId",
                                    )}
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </PackageSection>
            );
          })}
        </Box>
        {showSharedDetailsField && (
          <Box mt="24px" p="24px" bgcolor="#F4F4F9" borderRadius="8px">
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="details"
                  control={control}
                  defaultValue=""
                  rules={{
                    validate: (value) => {
                      const items = getValues("items") || [];
                      const anyItemWithReason = items.some(
                        (row) => !!row?.reason,
                      );
                      if (!anyItemWithReason) return true;
                      return value?.trim() ? true : t("resolve.required");
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      helperText={detailsError?.message}
                      error={!!detailsError}
                      label={`${t("resolve.form.details")}*`}
                      multiline
                      rows={4}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        <Box display="flex" mt="36px">
          <Box flex="0 0 20%" mr="20px">
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                resetClaimForm();
                setIsSelectAll(false);
                setActiveStep(0);
              }}
            >
              {t("resolve.form.back")}
            </Button>
          </Box>
          <Box flex="1">
            <LoadingButton
              disabled={!isDirty || !(insuranceAllowed || returnsAllowed)}
              loading={isSubmitting}
              variant="contained"
              fullWidth
              type="submit"
            >
              {t("resolve.form.submit")}
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </>
  );
}

function getFieldError(items, index, key) {
  return items?.[index]?.[key] ? true : false;
}

function getErrorMessage(items, index, key) {
  return (
    items?.[index]?.[key] && (
      <FormHelperText error>{items[index][key].message}</FormHelperText>
    )
  );
}
